import { useEffect, useState } from 'react'

import { useExternalAccountsQuery } from '@/graphql/generated/hooks'
import { StreamingType } from '@/graphql/generated/schemas'
import {
  OptionI,
  getDefaultExternalAccountOption,
  getExternalAccountOptions,
} from '@/utils/forms/optionBuilders'

import {
  getDefaultOrganizationId,
  getExternalAccount,
  getOrganizationId,
  shouldHandleExternalAccountField,
} from '../utils/utils'

type SetValueFnType = (
  name: string,
  value: unknown,
  config?: Partial<{
    shouldValidate: boolean
    shouldDirty: boolean
  }>
) => void

interface UseEditExternalAccountIProps {
  streamingType: OptionI<StreamingType>
  externalAccountId: OptionI
  setValue: SetValueFnType
  initialExternalAccountId: string
}

export const useEditExternalAccount = ({
  streamingType,
  externalAccountId,
  setValue,
  initialExternalAccountId,
}: UseEditExternalAccountIProps) => {
  const [organizationId, setOrganizationId] = useState(null)

  const shouldShowOrganizationId =
    Boolean(externalAccountId?.value) || Boolean(organizationId)
  const shouldShowExternalAccount = shouldHandleExternalAccountField(
    streamingType?.value
  )
  const { data, loading: isExternalAccountLoading } = useExternalAccountsQuery({
    fetchPolicy: 'network-only',
    skip: !shouldShowExternalAccount,
  })

  const defaultExternalAccount = getDefaultExternalAccountOption(data)

  const externalAccountOptions = getExternalAccountOptions(data)

  useEffect(() => {
    if (data && !isExternalAccountLoading) {
      setValue(
        'externalAccountId',
        getExternalAccount(externalAccountOptions, initialExternalAccountId)
      )
      setOrganizationId(
        getOrganizationId(data, externalAccountId) ??
          getDefaultOrganizationId(data)
      )
    }
  }, [isExternalAccountLoading, streamingType?.value])

  useEffect(() => {
    setOrganizationId(
      getOrganizationId(data, externalAccountId) ??
        getDefaultOrganizationId(data)
    )
  }, [externalAccountId?.value])

  return {
    externalAccountOptions,
    shouldShowExternalAccount,
    shouldShowOrganizationId,
    organizationId,
    defaultExternalAccount,
  }
}
